import { type RawTimeZone } from '@vvo/tzdb';
import { DateTime } from 'luxon';
import { Frequency as RRuleFrequency } from 'rrule/dist/esm/types';
import * as validator from 'validator';
import * as yup from 'yup';
import YupPassword from 'yup-password';
import 'yup-phone';
import { z } from 'zod';

import {
  type Camera,
  type Code,
  type CodeCategory,
  CodeCategoryViewMode,
  MonitoringHoursType,
  type Patient,
} from '../../models/index.js';
import { UpdateType } from '../../types/index.js';
import {
  CheckInDurations,
  CheckInIntervals,
  MAX_CHECK_IN_INTERVAL_HOURS,
  MIN_CHECK_IN_INTERVAL_HOURS,
  NUMBER_OF_DAYS_BEHIND,
} from '../MonitoringHours/index.js';


YupPassword(yup);

const cameraFormFields = {
  name: yup.string()
    .required('Name is a required field.')
    .min(3, 'Please provide a name that is longer than 3 characters.'),

  ipAddress: yup.string()
    .required('IP Address is a required field.')
    .test('ipAddress', 'IP Address is not valid', (value) => validator.isIP(value || '', 4)),
  port: yup.string().required('Port is a required field.'),
  username: yup.string().required('Username is a required field.'),
  vod: yup.string()
    .notRequired()
    .transform((value) => (value === '' ? undefined : value)),
  areaDescription: yup.string()
    .max(500, 'Area description must be at most 500 characters.')
    .notRequired()
    .transform((value) => (value === '' ? undefined : value)),
  irDualCapture: yup.boolean().notRequired(),
};

export const cameraFormSchema = yup.lazy(() => yup.object({
  ...cameraFormFields,
  password: yup.string().required('Password is a required field.'),
}));

export const updateCameraFormSchema = yup.lazy(() => yup.object({
  ...cameraFormFields,
  password: yup.string().notRequired(),
}));

export const categoryFormSchema = yup.object({
  name: yup.string()
    .required('First name is a required field.')
    .min(3, 'Please provide a first name that is longer than 3 characters.'),
  color: yup.string().required('Color is a required field.'),
  showInHeaderAs: yup.mixed<CodeCategoryViewMode>().notRequired().default(CodeCategoryViewMode.NONE),
  showInReport: yup.boolean().notRequired().default(false),
});

export const codeFormSchema = yup.object({
  code: yup.string().required('Code is a required field.'),
  description: yup.string().required('Description is a required field.'),
  category: yup.mixed<CodeCategory>().notRequired(),
  tags: yup.array().of(yup.string()).notRequired(),
});

export const codeMetadataFormSchema = yup.object({
  prompt: yup.string()
    .required('Prompt is a required field.')
    .min(3, 'Please provide a prompt that is longer than 3 characters.'),
  inputType: yup.string().required('Input type is a required field.'),
});

export const emergencyContactFormSchema = yup.object({
  firstName: yup.string()
    .required('First name is a required field.')
    .min(3, 'Please provide a first name that is longer than 3 characters.'),
  lastName: yup.string()
    .required('Last name is a required field.')
    .min(3, 'Please provide a last name that is longer than 3 characters.'),
  phoneNumber: yup.string()
    // .phone('US', false, 'Phone number is not a valid number.')
    .required('Phone number is a required field.'),
  relationship: yup.string().required('Please select a relationship status option.'),
});

export const logFormSchema = yup.object({
  createdAt: yup.mixed<DateTime>()
    .required("Log's Date and Time is a required field.")
    .test('is-before-10-days', "Log's date and time must not be past 10 days", function (value) {
      if (value === undefined) {
        return false;
      }
      return value.diffNow('minutes').minutes >= -10 * 24 * 60;
    })
    .test('is-in-future', "Log's date and time must not be in the future.", function (value) {
      if (value === undefined) {
        return false;
      }
      // in the past
      if (value.diffNow('minutes').minutes <= 0) {
        return true;
      }
      return !(value.diffNow('minutes').minutes > 0);
    }),
  code: yup.mixed<Code>().required('Code is a required field.'),
  metadata: yup.mixed<{ [id: string]: string }>().notRequired().default({}),
});
const luxonDateTime = z.custom<DateTime>(
  (val) => DateTime.isDateTime(val) && val.isValid,
  { message: 'Invalid date' },
);
export const monitoringHoursFormSchema = z.object({
  start: luxonDateTime.refine(
    (value) => {
      // For create events (no id), check if start time is not older than 31 days
      // Note: We can't access parent.id in Zod refinements easily, so this check
      // will be handled at the component level or we assume it's always valid here
      return value.diff(DateTime.now().startOf('day'), 'day').days >= NUMBER_OF_DAYS_BEHIND;
    },
    {message: 'Start time must not be older than 31 days'},
  ),
  end: luxonDateTime.refine((value) => Boolean(value), {message: 'End date is required'}),
  until: luxonDateTime.nullable(),
  repeats: z.boolean(),
  freq: z.enum(RRuleFrequency).nullable(),
  interval: z.number().nullable(),
  byDay: z.array(z.any()).nullable(),
  // until: z.date().nullable(),
  type: z.enum(MonitoringHoursType),
  checkInInterval: z.number().nullable(),
  checkInDuration: z.number().nullable(),
  updateType: z.enum(UpdateType),
}).superRefine((data, ctx) => {
  // Validate end is after start
  const {
    end,
    start,
    until,
    repeats,
    checkInDuration,
    type,
    freq,
    interval,
    byDay,
    checkInInterval,
  } = data;
  if (end && start) {
    if (end.diff(start, 'minutes').minutes <= 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'End time must be after start time',
        path: ['end'],
      });
    }
  }

  // Validate until is after end
  if (until && end) {
    if (until.diff(end, 'minutes').minutes <= 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Recurring date must be after end date',
        path: ['until'],
      });
    }
  }

  // When repeats is true, validate freq
  if (repeats) {
    if (freq === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'Frequency is required when repeating',
        path: ['freq'],
      });
    }

    // Validate interval
    if (!interval) {
      ctx.addIssue({
        code: 'custom',
        message: 'Interval must be a number',
        path: ['interval'],
      });
    } else if (interval < 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'Interval must be greater than 0',
        path: ['interval'],
      });
    }

    // Validate byDay for WEEKLY frequency
    if (freq === RRuleFrequency.WEEKLY) {
      if (!byDay || byDay.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'At least one weekday must be selected',
          path: ['byDay'],
        });
      }
    }
  }

  // When type is CHECK_IN, validate checkInInterval and checkInDuration
  if (type === MonitoringHoursType.CHECK_IN) {
    if (!checkInInterval) {
      ctx.addIssue({
        code: 'custom',
        message: 'Interval must be a number',
        path: ['checkInInterval'],
      });
    } else if (!CheckInIntervals.includes(checkInInterval)) {
      ctx.addIssue({
        code: 'custom',
        message: `Check-ins must run every ${MIN_CHECK_IN_INTERVAL_HOURS}–${MAX_CHECK_IN_INTERVAL_HOURS} hours`,
        path: ['checkInInterval'],
      });
    }

    if (!checkInDuration) {
      ctx.addIssue({
        code: 'custom',
        message: 'Check-in duration is required',
        path: ['checkInDuration'],
      });
    } else if (!CheckInDurations.includes(checkInDuration)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid check-in duration',
        path: ['checkInDuration'],
      });
    }
  }
});

export const patientFormSchema = yup.object({
  firstName: yup.string()
    .required('First name is a required field.')
    .min(3, 'Please provide a first name that is longer than 3 characters.'),
  lastName: yup.string()
    .required('Last name is a required field.')
    .min(3, 'Please provide a last name that is longer than 3 characters.'),
  physicalAddress: yup.string().required('Address is a required field.'),
  timeZone: yup.mixed<RawTimeZone>().required('Please select a timezone option.'),
  careInstructions: yup.string().notRequired().default(''),
  active: yup.boolean().default(false),
});

export const routerFormSchema = yup.object({
  name: yup.string()
    .required('Name is a required field.')
    .min(3, 'Please provide a name that is longer than 3 characters.'),
  imei: yup.string()
    .required('IMEI is a required field.')
    .test('imei', "IMEI is not valid, please use '##-######-######-#' format.", (value) =>
      validator.isIMEI(value as string, {allow_hyphens: true}),
    ),
  ipAddress: yup.string()
    .required('IP Address is a required field.')
    .test('ipAddress', 'IP Address is not valid', (value) => validator.isIP(value as string, 4)),
  username: yup.string().required('Username is a required field.'),
  password: yup.string().required('Password is a required field.'),
  patient: yup.mixed<Patient>().notRequired(),
  cameras: yup.array().of(yup.mixed<Camera>()).notRequired(),
});

export const addMonitorFormSchema = yup.object({
  name: yup.string()
    .required('Name is a required field.')
    .min(3, 'Please provide a name that is longer than 3 characters.'),
  email: yup.string().email("Please enter a valid Email.").required('Email is a required field.'),
  password: yup.string().password().required('Password is a required field.'),
});
export const updateMonitorFormSchema = yup.object().shape({
  name: yup.string()
    .required('Name is a required field.')
    .min(3, 'Please provide a name that is longer than 3 characters.'),
  email: yup.string().email("Please enter a valid Email.").required('Email is a required field.'),
  password: yup.string().when('password', (val) => {
    if (val?.length > 0 && !!val[0]) {
      return yup.string().password();
    } else {
      return yup.string().notRequired();
    }
  }),
}, [
  ["password", "password"],
]);

export const nvrFormSchema = yup.lazy(() => yup.object({
  name: yup.string()
    .required('Name is a required field.')
    .min(3, 'Please provide a name that is longer than 3 characters.'),
  ipAddress: yup.string()
    .required('IP Address is a required field.')
    .test('ipAddress', 'IP Address is not valid', (value) => validator.isIP(value as string, 4)),
  onvifPort: yup.string()
    .required('Onvif Port is a required field.')
    .matches(/^\d+$/, 'Onvif Port must be a number'),
  rtspPort: yup.string()
    .required('RTSP Port is a required field.')
    .matches(/^\d+$/, 'RTSP Port must be a number'),
  httpPort: yup.string()
    .notRequired()
    .matches(/^\d+$/, {excludeEmptyString: true, message: 'HTTP Port must be a number'}),
  username: yup.string().required('Username is a required field.'),
  password: yup.string().required('Password is a required field.'),
  patientId: yup.string().uuid().required('Patient is a required field.'),
}));
